import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import MealPlanView from '../../../Model/MealPlanView';
import { MealPlanType } from '../../../Model/MealPlan';
import Select, { ListSubheader, MenuItem } from '../../../../components/Select';
import texts from './texts.json';

const ChallengeConfigMealPlanSelector = ({
  mealPlans,
  selectedMealPlanId,
  onChange,
  isReadOnly,
}) => {
  const mealPlanOptions = useMemo(() => {
    const sortedMealPlans = mealPlans.slice().sort((a, b) => a.name.localeCompare(b.name));

    const options = sortedMealPlans
      .filter((mealPlan) => !mealPlan.isArchived && mealPlan.type !== MealPlanType.LIVE)
      .map((mealPlan) => ({
        value: mealPlan.id,
        label: mealPlan.name,
        isPublic: mealPlan.isPublic,
      }));

    return [
      {
        value: '',
        label: texts.none,
      },
      ...options,
    ];
  }, [mealPlans]);

  // Plans are told apart only when there are public ones to tell from the coach's own.
  const parseOptions = useCallback((options) => {
    const toMenuItem = ({ value, label }) => <MenuItem key={value} value={value}>{label}</MenuItem>;
    const publicOptions = options.filter(({ isPublic }) => isPublic);
    if (!publicOptions.length) {
      return options.map(toMenuItem);
    }

    const noneOption = options.filter(({ value }) => !value);
    const myOptions = options.filter(({ value, isPublic }) => value && !isPublic);
    return [
      ...noneOption.map(toMenuItem),
      ...(myOptions.length ? [<ListSubheader key="my-meal-plans">{texts.myMealPlans}</ListSubheader>] : []),
      ...myOptions.map(toMenuItem),
      <ListSubheader key="public-meal-plans">{texts.publicMealPlans}</ListSubheader>,
      ...publicOptions.map(toMenuItem),
    ];
  }, []);

  return (
    <Select
      value={selectedMealPlanId}
      optionParserFunction={parseOptions}
      onChange={(e) => onChange(mealPlans.find((mealPlan) => mealPlan.id === e.target.value))}
      options={mealPlanOptions}
      noOptionsText={texts.noMealPlansText}
      disabled={isReadOnly}
      label={texts.selectMealPlan}
    />
  );
};

ChallengeConfigMealPlanSelector.propTypes = {
  mealPlans: PropTypes.arrayOf(PropTypes.instanceOf(MealPlanView)),
  selectedMealPlanId: PropTypes.string,
  onChange: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

ChallengeConfigMealPlanSelector.defaultProps = {
  mealPlans: [],
  selectedMealPlanId: '',
  onChange: () => { },
  isReadOnly: false,
};

export default ChallengeConfigMealPlanSelector;
